/**
 * HANDLES-REQUIRED take-off (ADR 0064).
 *
 * The owner, 2026-08-09: *"the system should capture and report 'handles
 * required' as part of a summary report as this will feed purchasing and/or
 * the client provided handle form if the client is electing to provide
 * handles."*
 *
 * So this is a PURCHASING number, not a drawing statistic. Two things follow
 * from that, and they are the whole design:
 *
 *  1. **It applies the two-pull rule.** A 36" drawer face is one drawer and
 *     TWO handles. Counting drawer faces would under-order every wide bank in
 *     the job, which is exactly the failure mode a take-off exists to prevent.
 *  1b. **It counts DOORS as well as drawers** (ADR 0068). The owner,
 *     2026-08-09: a door section's left/right pair "will be hinged so require
 *     a handle on both doors". Until then this walked drawer banks only, so
 *     every door in the job ordered zero handles — the same under-ordering
 *     failure as (1), one layer up. One handle per HINGED leaf; a blind
 *     corner's false front is `hinged: false` and is excluded.
 *  2. **It shares `handlesForFace()` with the renderer** (src/geometry/handles.js)
 *     rather than re-deriving the count. A drawing that shows two pulls beside
 *     a report that says one is a defect you discover at the loading dock, and
 *     the only way to make that impossible is for both to be the same call.
 *
 * Pure: no DOM, no persistence, no mutation of the model it reads. Shaped like
 * `priceSpace(model, rules)` in src/pricing/space-pricing.js — the established
 * precedent in this repo for "walk the whole sketch and total something up" —
 * so it is node-testable against a real model without a browser.
 *
 * It reads the section's COMPOSITION (`sectionInterior`), not a solved
 * interior, for the same reason `storageSummary()` does: a handle count needs
 * each face's WIDTH and how many faces there are, and neither depends on the
 * vertical solve. Skipping the solve keeps a whole-sketch report cheap enough
 * to run on every repaint.
 */

import { deriveDoorGeometry, doorStyleByKey, hingedDoorLeaves } from '../geometry/doors.js';
import { drawerFaceWidthUnits, handleType, handlesForFace } from '../geometry/handles.js';
import { normalizeDoors } from '../geometry/interior.js';

/**
 * Every handle the sketch needs, totalled and broken down.
 *
 * @param {object} model the sketch model (createSketchModel's return).
 * @param {object} [rules] the resolved business rules. The handle rules are
 *   read straight off it — see handleRules() in src/geometry/handles.js.
 * @returns {{
 *   total:number,
 *   byType:Array<{type:string, label:string, count:number}>,
 *   byWall:Array<{wallId:string, label:string, count:number,
 *                 sections:Array<{sectionId:string, label:string,
 *                                 count:number, faces:number,
 *                                 faceWidthUnits:number, type:string}>}>,
 *   faces:number
 * }} `total` is the quantity to BUY. `faces` is the drawer-face count beside
 *   it, deliberately reported too: when the two differ, that difference is the
 *   two-pull rule doing its job, and a purchaser can see it rather than
 *   wonder. Walls and sections with no handles are omitted; the arrays are
 *   ordered the way the sketch is (wall number, then section order).
 */
export function handleTakeOff(model, rules = {}) {
  const byType = new Map();
  const byWall = [];
  let total = 0;
  let faces = 0;
  let doorLeafCount = 0;

  const hosts = [...new Set((model?.state?.sections ?? []).map((s) => s.wallId))];
  for (const wallId of hosts) {
    // `stacksForWall` is the canonical walls → sections → placed-units walk,
    // and the only one that pairs each unit with the layout item it sits in.
    const stacks = model.stacksForWall?.(wallId) ?? [];
    if (!stacks.length) continue;
    const sections = [];
    let wallCount = 0;

    /**
     * One thing that takes handles — a drawer face or a door leaf — folded
     * into every total at once, so a new KIND can never be added to the walk
     * while forgetting one of the four places it has to land.
     */
    const record = (sec, kind, typeKey, handleCount, widthUnits) => {
      if (!(handleCount > 0)) return;
      total += handleCount;
      wallCount += handleCount;
      if (kind === 'drawer') faces += 1; else doorLeafCount += 1;
      const t = handleType(typeKey, rules);
      const typeRow = byType.get(t.key) ?? { type: t.key, label: t.label, count: 0 };
      typeRow.count += handleCount;
      byType.set(t.key, typeRow);
      const existing = sections.find(
        (s) => s.sectionId === sec.id && s.type === t.key && s.kind === kind,
      );
      if (existing) {
        existing.count += handleCount;
        existing.faces += 1;
      } else {
        sections.push({
          sectionId: sec.id,
          label: sec.typeCode ?? 'section',
          kind,
          count: handleCount,
          faces: 1,
          faceWidthUnits: widthUnits,
          type: t.key,
        });
      }
    };

    for (const stack of stacks) {
      (stack.units ?? []).forEach((unit, k) => {
        const sec = unit.section;
        if (!sec) return;
        // The RUN section spans the solved opening; a STACKED unit carries its
        // own span. The same ternary space-pricing.js uses — a stacked unit is
        // not guaranteed to inherit the opening below it.
        const openingUnits = k === 0 ? stack.item?.openingUnits : unit.widthUnits;
        if (!(openingUnits > 0)) return;
        const faceWidthUnits = drawerFaceWidthUnits(openingUnits, rules);
        const interior = model.sectionInterior?.(sec) ?? [];
        for (const el of interior) {
          if (el?.kind !== 'drawerBank') continue;
          for (const d of el.drawers ?? []) {
            if (!(d?.heightUnits > 0)) continue;
            const handles = handlesForFace(
              { widthUnits: faceWidthUnits, heightUnits: d.heightUnits, type: el.handleType },
              rules,
            );
            record(sec, 'drawer', el.handleType, handles.length, faceWidthUnits);
          }
        }

        // DOORS. Every hinged leaf takes a handle (owner, 2026-08-09: a
        // left/right pair "will be hinged so require a handle on both doors").
        // A door leaf gets ONE handle — the two-pull rule is a drawer rule,
        // keyed off a 32" FACE, which is not a case the owner has described
        // for doors. Counted from the same `doorLeaves` the elevation draws
        // (src/geometry/doors.js), so the drawing and the purchase order
        // cannot disagree. ADR 0068.
        const doors = normalizeDoors(sec.doors);
        const style = doorStyleByKey(rules.doorStyles, doors?.styleKey);
        const feed = model.interiorForUnit?.(unit);
        const geometry = doors?.fromElementId
          ? deriveDoorGeometry(doors, feed?.solved?.elements, openingUnits, style, rules)
          : null;
        const doorHandleLeaves = geometry?.ok
          ? geometry.bands.flatMap((_, part) => geometry.leaves.map((leaf) => ({ ...leaf, part })))
          : hingedDoorLeaves(doors, openingUnits, rules);
        for (const leaf of doorHandleLeaves) {
          record(sec, 'door', doors.handleType, 1, leaf.widthUnits);
        }
      });
    }
    if (wallCount > 0) {
      byWall.push({
        wallId,
        label: wallLabel(model, wallId),
        count: wallCount,
        sections,
      });
    }
  }

  byWall.sort((a, b) => a.label.localeCompare(b.label, 'en', { numeric: true }));
  return {
    total,
    faces,
    doorLeaves: doorLeafCount,
    byType: [...byType.values()].sort((a, b) => b.count - a.count || a.type.localeCompare(b.type)),
    byWall,
  };
}

/** "W3" the way every other summary names a wall, falling back to the id. */
function wallLabel(model, wallId) {
  const wall = (model?.state?.walls ?? []).find((w) => w.id === wallId);
  return wall?.wallNumber ? `W${wall.wallNumber}` : `W?`;
}
